import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import api from '../../helpers/api_helper';

export interface ManualDeposit {
  id: number;
  user: number;
  user_mobile: string;
  bank: number | null;
  bank_name: string | null;
  mode: 'upi' | 'qr' | 'imps_neft_rtgs' | 'payxmint' | 'payxmint_v2' | 'ismartpay';
  amount: string;
  user_account_name: string;
  user_bank_name: string;
  user_account_number: string;
  user_ifsc_code: string;
  user_upi_handle: string | null;
  utr_number: string;
  screenshot: string | null;
  screenshot_url?: string | null;
  status: 'pending' | 'approved' | 'rejected';
  reviewed_by: number | null;
  reviewed_by_name?: string | null;
  reviewed_by_role?: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
  request_type?: 'manual' | 'gateway';
  gateway?: string | null;
  gateway_order_id?: string | null;
  gateway_status?: string | null;
}

export interface DepositsStats {
  overall: {
    total_deposits: number;
    pending_deposits: number;
    approved_deposits: number;
    rejected_deposits: number;
    total_amount: number;
    pending_amount: number;
    approved_amount: number;
  };
  recent: {
    today_deposits: number;
    week_deposits: number;
    month_deposits: number;
  };
  mode_breakdown: Array<{
    mode: string;
    count: number;
    total_amount: number;
  }>;
}

export interface FetchParams {
  status?: 'pending' | 'approved' | 'rejected';
  mode?: 'upi' | 'qr' | 'imps_neft_rtgs' | 'payxmint' | 'payxmint_v2' | 'ismartpay';
  date_from?: string; // YYYY-MM-DD
  date_to?: string;   // YYYY-MM-DD
  search?: string;
  page?: number;
  page_size?: number;
}

export const fetchManualDeposits = createAsyncThunk(
  'manualDeposits/fetch',
  async (params: FetchParams | undefined, { rejectWithValue }) => {
    try {
      const query: string[] = [];
      if (params?.status) query.push(`status=${params.status}`);
      if (params?.mode) query.push(`mode=${params.mode}`);
      if (params?.date_from) query.push(`date_from=${params.date_from}`);
      if (params?.date_to) query.push(`date_to=${params.date_to}`);
      if (params?.search) query.push(`search=${encodeURIComponent(params.search)}`);
      if (params?.page) query.push(`page=${params.page}`);
      if (params?.page_size) query.push(`page_size=${params.page_size}`);

      const qs = query.length ? `?${query.join('&')}` : '';
      const res = await api.get(`/finance/manual-deposits/${qs}`);

      return {
        items: res.data?.results?.deposits ?? [],
        count: Number(res.data?.count ?? 0),
        next: res.data?.next ?? null,
        previous: res.data?.previous ?? null,
      };
    } catch (e: any) {
      return rejectWithValue(e.response?.data?.detail || e.message);
    }
  }
);

export const fetchManualDepositsStats = createAsyncThunk(
  'manualDeposits/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get('/finance/manual-deposits/stats/');
      // API returns { success: boolean, stats: {...} }
      // We only need the stats object in state
      return res.data?.stats ?? res.data;
    } catch (e: any) {
      return rejectWithValue(e.response?.data?.detail || e.message);
    }
  }
);

export interface ReviewPayload {
  id: number;
  status: 'approved' | 'rejected';
  admin_notes?: string;
  rejection_reason?: string;
}

export const reviewManualDeposit = createAsyncThunk(
  'manualDeposits/review',
  async ({ id, ...data }: ReviewPayload, { rejectWithValue }) => {
    try {
      const res = await api.post(`/finance/manual-deposits/${id}/review/`, data);
      // API returns { success, message, deposit: {...} }
      // Extract the deposit object from the response
      return res.data?.deposit ?? res.data;
    } catch (e: any) {
      return rejectWithValue(e.response?.data?.detail || e.message);
    }
  }
);


export const verifyPayxMintPayment = createAsyncThunk(
  'manualDeposits/verifyPayxMint',
  async (id: number, { rejectWithValue }) => {
    try {
      const res = await api.post(`/finance/payxmint/${id}/verify/`, {});
      return res.data;
    } catch (e: any) {
      return rejectWithValue(
        e.response?.data?.message ||
        e.response?.data?.detail ||
        e.message
      );
    }
  }
);


export const recheckPayxMintV2Payment = createAsyncThunk(
  'manualDeposits/recheckPayxMintV2',
  async (id: number, { rejectWithValue }) => {
    try {
      const res = await api.post(
        `/finance/payxmint/${id}/recheck/`,
        {}
      );
      return res.data;
    } catch (e: any) {
      return rejectWithValue(
        e.response?.data?.error ||
        e.response?.data?.message ||
        e.response?.data?.detail ||
        e.message
      );
    }
  }
);

// This action must not be exposed as an enabled recovery button
// until the backend recovery workflow is fully tested and authorized.
export const recoverPayxMintV2Wallet = createAsyncThunk(
  'manualDeposits/recoverPayxMintV2',
  async (id: number, { rejectWithValue }) => {
    try {
      const res = await api.post(
        `/finance/payxmint/${id}/recover/`,
        {}
      );
      return res.data;
    } catch (e: any) {
      return rejectWithValue(
        e.response?.data?.error ||
        e.response?.data?.message ||
        e.response?.data?.detail ||
        e.message
      );
    }
  }
);


export const approvePayxMintV2Recovery = createAsyncThunk(
  'manualDeposits/approvePayxMintV2Recovery',
  async (id: number, { rejectWithValue }) => {
    try {
      const res = await api.post(
        `/finance/payxmint/${id}/approve-recovery/`,
        {}
      );
      return res.data;
    } catch (e: any) {
      return rejectWithValue(
        e.response?.data?.error ||
        e.response?.data?.message ||
        e.response?.data?.detail ||
        e.message
      );
    }
  }
);

export const revokePayxMintV2Recovery = createAsyncThunk(
  'manualDeposits/revokePayxMintV2Recovery',
  async (id: number, { rejectWithValue }) => {
    try {
      const res = await api.post(
        `/finance/payxmint/${id}/revoke-recovery/`,
        {}
      );
      return res.data;
    } catch (e: any) {
      return rejectWithValue(
        e.response?.data?.error ||
        e.response?.data?.message ||
        e.response?.data?.detail ||
        e.message
      );
    }
  }
);

export interface ManualApproveGatewayPayload {
  id: number;
  utr: string;
  reason: string;
}

export const manualApproveGatewayPayment = createAsyncThunk(
  'manualDeposits/manualApproveGateway',
  async (
    { id, utr, reason }: ManualApproveGatewayPayload,
    { rejectWithValue }
  ) => {
    try {
      const res = await api.post(
        `/finance/gateway-payments/${id}/manual-approve/`,
        { utr, reason }
      );

      return res.data;
    } catch (e: any) {
      return rejectWithValue(
        e.response?.data?.message ||
        e.response?.data?.detail ||
        e.message
      );
    }
  }
);

interface State {
  items: ManualDeposit[];
  stats: DepositsStats | null;
  loading: boolean;
  error: string | null;
  activeStatus: 'all' | 'pending' | 'approved' | 'rejected';
  count: number;
  next: string | null;
  previous: string | null;
  currentRequestId: string | null;
}

const initialState: State = {
  items: [],
  stats: null,
  loading: false,
  error: null,
  activeStatus: 'all',
  count: 0,
  next: null,
  previous: null,
  currentRequestId: null,
};

const slice = createSlice({
  name: 'manualDeposits',
  initialState,
  reducers: {
    setActiveStatus(state, action: PayloadAction<State['activeStatus']>) {
      state.activeStatus = action.payload;
    },
    upsertDeposit(state, action: PayloadAction<ManualDeposit>) {
      const idx = state.items.findIndex(d => d.id === action.payload.id);
      if (idx >= 0) state.items[idx] = action.payload;
      else state.items.unshift(action.payload);
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchManualDeposits.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchManualDeposits.fulfilled, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.currentRequestId = null;
        state.items = action.payload.items as ManualDeposit[];
        state.count = action.payload.count;
        state.next = action.payload.next;
        state.previous = action.payload.previous;
      })
      .addCase(fetchManualDeposits.rejected, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.currentRequestId = null;
        state.error = action.payload as string;
      })
      .addCase(fetchManualDepositsStats.fulfilled, (state, action) => {
        state.stats = action.payload as DepositsStats;
      })
      .addCase(reviewManualDeposit.fulfilled, (state, action) => {
        const updated = action.payload as ManualDeposit;
        const idx = state.items.findIndex(d => d.id === updated.id);
        if (idx >= 0) state.items[idx] = updated;
      });
  }
});

export const { setActiveStatus, upsertDeposit } = slice.actions;
export default slice.reducer;


